import type { Partner } from "@expert/shared-types";
import { create } from "zustand";
import { devtools } from "zustand/middleware";
import { type Contact, filterContacts as filterContactsByPartner } from "@/shared-types";
import { getContacts } from "../api/contactsApiClient";

interface VdnContactsState {
    contacts: Contact[];
    contactNumbersSet: Set<string>;
    isFetching: boolean;
}
interface VdnContactsActions {
    resetState: () => void;
    getAndSetContacts: (partner: Partner) => Promise<void>;
    addContact: (contact: Contact) => void;
}

type VdnContactsStateStore = VdnContactsState & VdnContactsActions;

const defaultState: VdnContactsState = {
    contacts: [],
    contactNumbersSet: new Set(),
    isFetching: false,
};

export const useContactsStateStore = create<VdnContactsStateStore>()(
    devtools(
        (set, get) => ({
            ...defaultState,

            resetState: () => set(() => ({ ...defaultState }), false, "resetState"),

            getAndSetContacts: async (partner: Partner) => {
                if (get().isFetching) return;
                set({ isFetching: true }, false, "getAndSetContacts-start");

                try {
                    const allContacts = await getContacts();
                    const filteredContacts = filterContactsByPartner(allContacts, partner);

                    for (const contact of filteredContacts) {
                        get().addContact(contact);
                    }

                    set({ isFetching: false }, false, "getAndSetContacts-end");
                } catch (error) {
                    console.error("Failed to fetch contacts:", error);
                    set({ isFetching: false }, false, "getAndSetContacts-error");
                }
            },

            addContact: (contact: Contact) => {
                set(
                    (state) => {
                        if (state.contactNumbersSet.has(contact.number)) {
                            // Skip duplicate contacts to prevent breaking autocomplete component.
                            return state;
                        }
                        const updatedNumbersSet = new Set(state.contactNumbersSet);
                        updatedNumbersSet.add(contact.number);
                        return {
                            contacts: [...state.contacts, contact],
                            contactNumbersSet: updatedNumbersSet,
                        };
                    },
                    false,
                    "addContact",
                );
            },
        }),
        {
            enabled: import.meta.env.VITE_ENV_TYPE !== "production",
            store: "transfers",
            name: "workspace/call-controls",
        },
    ),
);
